import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { projectService, type ProjectForm } from '@services/projectService';
import {
  ensureProjectBucket,
  isProjectBucketFresh,
  markBucketError,
  markBucketLoading,
  type ProjectBucket,
} from '../utils/projectBucket';
import type { RootState } from '../index';

interface ProjectFormsBucket extends ProjectBucket {
  items: ProjectForm[];
}

interface ProjectFormsState {
  byProjectId: Record<string, ProjectFormsBucket>;
  deletedByProjectId: Record<string, ProjectFormsBucket>;
}

const createBucket = (): ProjectFormsBucket => ({
  items: [],
  isLoading: false,
  error: null,
  lastFetched: null,
});

const initialState: ProjectFormsState = {
  byProjectId: {},
  deletedByProjectId: {},
};

export const fetchProjectForms = createAsyncThunk(
  'projectForms/fetchProjectForms',
  async (
    {
      projectId,
      force,
      includeDeleted,
    }: { projectId: string; force?: boolean; includeDeleted?: boolean },
    { getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const includeDeletedForms = Boolean(includeDeleted);
    const bucket = includeDeletedForms
      ? state.projectForms.deletedByProjectId[projectId]
      : state.projectForms.byProjectId[projectId];

    if (isProjectBucketFresh(bucket, force)) {
      return { projectId, items: null as ProjectForm[] | null };
    }

    try {
      const items = await projectService.getForms(projectId, {
        includeDeleted: includeDeletedForms,
      });
      return { projectId, items, includeDeleted: includeDeletedForms };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load project forms';
      return rejectWithValue({ projectId, message, includeDeleted: includeDeletedForms });
    }
  }
);

export const createProjectForm = createAsyncThunk(
  'projectForms/createProjectForm',
  async (
    {
      projectId,
      data,
    }: {
      projectId: string;
      data: Omit<ProjectForm, 'id'>;
    },
    { rejectWithValue }
  ) => {
    try {
      const created = await projectService.createForm(projectId, data);
      return { projectId, form: created };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create form';
      return rejectWithValue({ projectId, message });
    }
  }
);

export const updateProjectForm = createAsyncThunk(
  'projectForms/updateProjectForm',
  async (
    {
      projectId,
      formId,
      data,
    }: {
      projectId: string;
      formId: string;
      data: Partial<ProjectForm>;
    },
    { rejectWithValue }
  ) => {
    try {
      const updated = await projectService.updateForm(projectId, formId, data);
      return { projectId, form: updated };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update form';
      return rejectWithValue({ projectId, message });
    }
  }
);

export const deleteProjectForm = createAsyncThunk(
  'projectForms/deleteProjectForm',
  async (
    {
      projectId,
      formId,
    }: {
      projectId: string;
      formId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      await projectService.deleteForm(projectId, formId);
      return { projectId, formId };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete form';
      return rejectWithValue({ projectId, message });
    }
  }
);

export const permanentlyDeleteProjectForm = createAsyncThunk(
  'projectForms/permanentlyDeleteProjectForm',
  async (
    {
      projectId,
      formId,
    }: {
      projectId: string;
      formId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      await projectService.permanentlyDeleteForm(projectId, formId);
      return { projectId, formId };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to permanently delete form';
      return rejectWithValue({ projectId, message });
    }
  }
);

const projectFormsSlice = createSlice({
  name: 'projectForms',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchProjectForms.pending, (state, action) => {
        const target = action.meta.arg.includeDeleted
          ? state.deletedByProjectId
          : state.byProjectId;
        const bucket = ensureProjectBucket(target, action.meta.arg.projectId, createBucket);
        markBucketLoading(bucket);
      })
      .addCase(fetchProjectForms.fulfilled, (state, action) => {
        const target = action.payload.includeDeleted ? state.deletedByProjectId : state.byProjectId;
        const bucket = ensureProjectBucket(target, action.payload.projectId, createBucket);
        bucket.isLoading = false;

        if (action.payload.items !== null) {
          bucket.items = action.payload.items;
          bucket.lastFetched = Date.now();
        }
      })
      .addCase(fetchProjectForms.rejected, (state, action) => {
        const payload = action.payload as
          | { projectId?: string; message?: string; includeDeleted?: boolean }
          | undefined;
        const projectId = payload?.projectId ?? action.meta.arg.projectId;
        const target = payload?.includeDeleted ? state.deletedByProjectId : state.byProjectId;
        const bucket = ensureProjectBucket(target, projectId, createBucket);
        markBucketError(bucket, payload?.message ?? 'Failed to load project forms');
      })
      .addCase(createProjectForm.fulfilled, (state, action) => {
        const { projectId, form } = action.payload;
        const bucket = ensureProjectBucket(state.byProjectId, projectId, createBucket);
        bucket.items = [form, ...bucket.items];
      })
      .addCase(updateProjectForm.fulfilled, (state, action) => {
        const { projectId, form } = action.payload;
        const bucket = ensureProjectBucket(state.byProjectId, projectId, createBucket);
        bucket.items = bucket.items.map(item => (item.id === form.id ? form : item));
      })
      .addCase(deleteProjectForm.fulfilled, (state, action) => {
        const { projectId, formId } = action.payload;
        const bucket = ensureProjectBucket(state.byProjectId, projectId, createBucket);
        bucket.items = bucket.items.filter(item => item.id !== formId);

        const deletedBucket = ensureProjectBucket(
          state.deletedByProjectId,
          projectId,
          createBucket
        );
        deletedBucket.lastFetched = null;
      })
      .addCase(permanentlyDeleteProjectForm.fulfilled, (state, action) => {
        const { projectId, formId } = action.payload;
        const bucket = ensureProjectBucket(state.byProjectId, projectId, createBucket);
        bucket.items = bucket.items.filter(item => item.id !== formId);

        const deletedBucket = ensureProjectBucket(
          state.deletedByProjectId,
          projectId,
          createBucket
        );
        deletedBucket.items = deletedBucket.items.filter(item => item.id !== formId);
      });
  },
});

export const selectProjectFormsByProjectId = (state: RootState, projectId: string) =>
  state.projectForms.byProjectId[projectId]?.items ?? [];

export const selectProjectFormsLoading = (state: RootState, projectId: string) =>
  state.projectForms.byProjectId[projectId]?.isLoading ?? false;

export const selectDeletedProjectFormsByProjectId = (state: RootState, projectId: string) =>
  state.projectForms.deletedByProjectId[projectId]?.items ?? [];

export const selectDeletedProjectFormsLoading = (state: RootState, projectId: string) =>
  state.projectForms.deletedByProjectId[projectId]?.isLoading ?? false;

export default projectFormsSlice.reducer;
